import React, { useMemo } from 'react';

import {
  GENERIC_FORM_TYPES,
  LockboxCheckType,
  lockboxReasonTitle,
  LockboxStatus,
  LockboxStatusReason,
  lockboxStatusTitle,
  LockboxType,
} from '@/modules/employer/components/Lockbox/lockbox.types';

import AllocateFullCheck from './components/AllocateFullCheck/AllocateFullCheck';
import ForBenefitOf from './components/ForBenefitOf';
import SelectAccount from './components/SelectAccount';
import SendRemainderBack from './components/SendRemainderBack/SendRemainderBack';

interface Props {
  lockboxType?: LockboxType;
  isClaimRepaymentApproveDisabled?: boolean;
  isClaimRepaymentPartiallyApproveDisabled?: boolean;
  isClaimRepaymentFullCheckApprove?: boolean;
  currentLockboxStatus?: LockboxStatus;
  hasCreatedRepaymentAmount?: boolean;
  isReturnedChecksEnabled?: boolean;
  selectedCheckType?: LockboxCheckType;
}

const useLockboxProcessingOptions = ({
  lockboxType,
  isClaimRepaymentApproveDisabled,
  isClaimRepaymentPartiallyApproveDisabled,
  isClaimRepaymentFullCheckApprove,
  currentLockboxStatus,
  hasCreatedRepaymentAmount,
  isReturnedChecksEnabled,
  selectedCheckType,
}: Props) => {
  const options = useMemo(() => {
    if (lockboxType === LockboxType.RETURNED_CHECK && isReturnedChecksEnabled) {
      const pendOption = {
        key: LockboxStatus.PENDING,
        title: lockboxStatusTitle[LockboxStatus.PENDING],
        noteTitle: 'Note',
        disabled: true,
      };
      const cancelOption = {
        key: LockboxStatus.CANCELED,
        title: lockboxStatusTitle[LockboxStatus.CANCELED],
        noteTitle: 'Note',
        disabled: true,
      };

      if (!selectedCheckType) {
        return [pendOption, cancelOption];
      }

      if (selectedCheckType === LockboxCheckType.LOCKBOX) {
        return [
          {
            key: LockboxStatus.VOID,
            title:  lockboxStatusTitle[LockboxStatus.VOID],
          },
          {
            key: LockboxStatus.REISSUE,
            title:  lockboxStatusTitle[LockboxStatus.REISSUE],
            children: <SendRemainderBack key={currentLockboxStatus} currentLockboxStatus={currentLockboxStatus} />,
          },
          pendOption,
          cancelOption,
        ];
      }

      return [
        {
          key: LockboxStatus.PROCESS,
          title: lockboxStatusTitle[LockboxStatus.PROCESS],
        },
        pendOption,
        cancelOption,
      ];
    }

    if (lockboxType === LockboxType.RETURNED_MAIL) {
      return [
        {
          key: LockboxStatus.APPROVED,
          title: 'Process, mark address as bad',
        },
        {
          key: LockboxStatus.PENDING,
          title: lockboxStatusTitle[LockboxStatus.PENDING],
          noteTitle: 'Note',
        },
        {
          key: LockboxStatus.CANCELED,
          title: lockboxStatusTitle[LockboxStatus.CANCELED],
          noteTitle: 'Note',
        },
      ];
    }

    if (lockboxType && GENERIC_FORM_TYPES.includes(lockboxType)) {
      return [
        {
          key: LockboxStatus.PROCESSED_MANUALLY,
          title: lockboxStatusTitle[LockboxStatus.PROCESSED_MANUALLY],
          noteTitle: 'Note',
        },
        {
          key: LockboxStatus.PENDING,
          title: lockboxStatusTitle[LockboxStatus.PENDING],
          optionsTitle: 'Select the reason',
          noteTitle: 'Note',
          subOptions: [
            {
              key: LockboxStatusReason.PARTICIPANT_NOT_FOUND,
              title: lockboxReasonTitle[LockboxStatusReason.PARTICIPANT_NOT_FOUND],
            },
            {
              key: LockboxStatusReason.ACCOUNT_NOT_FOUND,
              title: lockboxReasonTitle[LockboxStatusReason.ACCOUNT_NOT_FOUND],
            },
            {
              key: LockboxStatusReason.INVESTMENTS_LIQUIDATION_REQUIRED,
              title: lockboxReasonTitle[LockboxStatusReason.INVESTMENTS_LIQUIDATION_REQUIRED],
            },
            {
              key: LockboxStatusReason.ACCOUNT_CLOSED,
              title: lockboxReasonTitle[LockboxStatusReason.ACCOUNT_CLOSED],
            },
            {
              key: LockboxStatusReason.INSUFFICIENT_FUNDS,
              title: lockboxReasonTitle[LockboxStatusReason.INSUFFICIENT_FUNDS],
            },
            {
              key: LockboxStatusReason.OTHER,
              title: lockboxReasonTitle[LockboxStatusReason.OTHER],
            }
          ],
        },
        {
          key: LockboxStatus.CANCELED,
          title: lockboxStatusTitle[LockboxStatus.CANCELED],
          noteTitle: 'Note',
        },
      ];
    }

    if (lockboxType === LockboxType.CLAIM_REPAYMENT) {
      return [
        {
          key: LockboxStatus.APPROVED,
          title: lockboxStatusTitle[LockboxStatus.APPROVED],
          disabled: isClaimRepaymentApproveDisabled,
          ...isClaimRepaymentFullCheckApprove ? { children: <AllocateFullCheck /> } : {},
        },
        {
          key: LockboxStatus.PARTIALLY_APPROVED,
          title: lockboxStatusTitle[LockboxStatus.PARTIALLY_APPROVED],
          optionsTitle: 'What do you want to do with remainder?',
          disabled: isClaimRepaymentPartiallyApproveDisabled,
          subOptions: [
            {
              key: LockboxStatusReason.SEND_THE_REMAINDER_BACK,
              title: lockboxReasonTitle[LockboxStatusReason.SEND_THE_REMAINDER_BACK],
              children: <SendRemainderBack key={currentLockboxStatus} currentLockboxStatus={currentLockboxStatus} />,
            },
            {
              key: LockboxStatusReason.ALLOCATE_REMAINDER_TO_THE_ACCOUNT,
              title: lockboxReasonTitle[LockboxStatusReason.ALLOCATE_REMAINDER_TO_THE_ACCOUNT],
              children: <SelectAccount />,
            },
          ]
        },
        ...hasCreatedRepaymentAmount ? [] : [{
          key: LockboxStatus.DECLINED,
          title: lockboxStatusTitle[LockboxStatus.DECLINED],
          optionsTitle: 'Select the reason',
          noteTitle: 'Note',
          children: <SendRemainderBack key={currentLockboxStatus} currentLockboxStatus={currentLockboxStatus} />,
          subOptions: [
            {
              key: LockboxStatusReason.CLAIM_NOT_FOUND,
              title: lockboxReasonTitle[LockboxStatusReason.CLAIM_NOT_FOUND],
            },
            {
              key: LockboxStatusReason.PARTICIPANT_NOT_FOUND,
              title: 'Employee can’t be found',
            },
            {
              key: LockboxStatusReason.OTHER,
              title: lockboxReasonTitle[LockboxStatusReason.OTHER],
            },
          ],
        }],
        {
          key: LockboxStatus.PENDING,
          title: lockboxStatusTitle[LockboxStatus.PENDING],
          optionsTitle: 'Select the reason',
          noteTitle: 'Note',
          subOptions: [
            {
              key: LockboxStatusReason.CLAIM_NOT_FOUND,
              title: lockboxReasonTitle[LockboxStatusReason.CLAIM_NOT_FOUND],
            },
            {
              key: LockboxStatusReason.PARTICIPANT_NOT_FOUND,
              title: 'Employee can’t be found',
            },
            {
              key: LockboxStatusReason.OTHER,
              title: lockboxReasonTitle[LockboxStatusReason.OTHER],
            },
          ],
        },
        ...hasCreatedRepaymentAmount ? [] : [{
          key: LockboxStatus.CANCELED,
          title: lockboxStatusTitle[LockboxStatus.CANCELED],
          noteTitle: 'Note',
        }]
      ];
    }

    return [
      {
        key: LockboxStatus.APPROVED,
        title: lockboxStatusTitle[LockboxStatus.APPROVED],
        optionsTitle: 'Select payment method',
        ...(lockboxType === LockboxType.OUTBOUND_TOA ? {
            subOptions: [
              {
                key: LockboxStatusReason.CHECK,
                title: lockboxReasonTitle[LockboxStatusReason.CHECK],
                children: <ForBenefitOf />,
              },
              {
                key: LockboxStatusReason.WIRE,
                title: lockboxReasonTitle[LockboxStatusReason.WIRE],
              }
            ],
          } : {}),
      },
      {
        key: LockboxStatus.DECLINED,
        title: lockboxStatusTitle[LockboxStatus.DECLINED],
        optionsTitle: 'Select the reason',
        noteTitle: 'Note',
        subOptions: [
          {
            key: LockboxStatusReason.PARTICIPANT_NOT_FOUND,
            title: lockboxReasonTitle[LockboxStatusReason.PARTICIPANT_NOT_FOUND],
          },
          {
            key: LockboxStatusReason.ACCOUNT_NOT_FOUND,
            title: lockboxReasonTitle[LockboxStatusReason.ACCOUNT_NOT_FOUND],
          },
          {
            key: LockboxStatusReason.INVESTMENTS_LIQUIDATION_REQUIRED,
            title: lockboxReasonTitle[LockboxStatusReason.INVESTMENTS_LIQUIDATION_REQUIRED],
          },
          {
            key: LockboxStatusReason.ACCOUNT_CLOSED,
            title: lockboxReasonTitle[LockboxStatusReason.ACCOUNT_CLOSED],
          },
          {
            key: LockboxStatusReason.INSUFFICIENT_FUNDS,
            title: lockboxReasonTitle[LockboxStatusReason.INSUFFICIENT_FUNDS],
          },
          {
            key: LockboxStatusReason.OTHER,
            title: lockboxReasonTitle[LockboxStatusReason.OTHER],
          }
        ],
        children: <ForBenefitOf />,
      },
      {
        key: LockboxStatus.PENDING,
        title: lockboxStatusTitle[LockboxStatus.PENDING],
        optionsTitle: 'Select the reason',
        noteTitle: 'Note',
        subOptions: [
          {
            key: LockboxStatusReason.PARTICIPANT_NOT_FOUND,
            title: lockboxReasonTitle[LockboxStatusReason.PARTICIPANT_NOT_FOUND],
          },
          {
            key: LockboxStatusReason.ACCOUNT_NOT_FOUND,
            title: lockboxReasonTitle[LockboxStatusReason.ACCOUNT_NOT_FOUND],
          },
          {
            key: LockboxStatusReason.INVESTMENTS_LIQUIDATION_REQUIRED,
            title: lockboxReasonTitle[LockboxStatusReason.INVESTMENTS_LIQUIDATION_REQUIRED],
          },
          {
            key: LockboxStatusReason.ACCOUNT_CLOSED,
            title: lockboxReasonTitle[LockboxStatusReason.ACCOUNT_CLOSED],
          },
          {
            key: LockboxStatusReason.INSUFFICIENT_FUNDS,
            title: lockboxReasonTitle[LockboxStatusReason.INSUFFICIENT_FUNDS],
          },
          {
            key: LockboxStatusReason.OTHER,
            title: lockboxReasonTitle[LockboxStatusReason.OTHER],
          }
        ],
      },
      {
        key: LockboxStatus.CANCELED,
        title: lockboxStatusTitle[LockboxStatus.CANCELED],
        noteTitle: 'Note',
      }
    ];
  }, [
    lockboxType,
    isClaimRepaymentApproveDisabled,
    isClaimRepaymentPartiallyApproveDisabled,
    isClaimRepaymentFullCheckApprove,
    currentLockboxStatus,
    hasCreatedRepaymentAmount,
    isReturnedChecksEnabled,
    selectedCheckType,
    ]
  );

  return {
    options
  };
};

export default useLockboxProcessingOptions;
